import { Box, Skeleton, Typography } from '@mui/material';
import { Appointment, GeneralRequest } from '../../interfaces';
import HorizontalCardList from '../global/HorizontalCardList';
import useGetUnreadRequests from '../../hooks/requests/useGetUnreadRequests';
import type { Tab } from '../../routes/RequestsPage';

interface Props {
  requests: (Appointment | GeneralRequest)[];
  tab: Tab;
  isLoading: boolean;
  onOpenRequest: (request: Appointment | GeneralRequest) => void;
  onEndReached: () => void | Promise<void>;
}

function RequestsList({
  requests,
  tab,
  isLoading,
  onOpenRequest,
  onEndReached,
}: Props) {
  const { data: unreadRequests } = useGetUnreadRequests();

  if (!requests && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          gap: 10,
        }}>
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
      </Box>
    );
  }

  if (requests?.length === 0) {
    return (
      <Typography variant="body1">
        {`No ${
          tab === 'REQUESTED' ? 'new' : tab.toLowerCase()
        } requests to show right now. If you believe this is an error, please reach out to our support team.`}
      </Typography>
    );
  }

  const appointments = (requests ?? [])
    .map(request => {
      if (
        (request as Appointment).status &&
        (request as Appointment).status === tab
      ) {
        return request;
      }
      // A declined offer stays accepted but belongs with the new requests.
      // The flag outlives the decline, so the status has to be checked too.
      if (
        tab === 'REQUESTED' &&
        (request as Appointment).status === 'ACCEPTED' &&
        (request as Appointment).offeredTimesDeclinedAt
      ) {
        return request;
      }
      if ((request as GeneralRequest).requestId && tab === 'REQUESTED') {
        return request;
      }
      return null;
    })
    .filter(Boolean) as (Appointment | GeneralRequest)[];

  return (
    <HorizontalCardList
      appointments={appointments}
      onOpenRequest={onOpenRequest}
      unreadRequests={unreadRequests}
      onEndReached={onEndReached}
    />
  );
}

export default RequestsList;
